import { getConfigValue } from "../config/appConfig";

const getClientId = () => getConfigValue("REACT_APP_GOOGLE_CLIENT_ID", "");
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const TOKEN_KEY = "gdrive_access_token";
const TOKEN_EXP_KEY = "gdrive_token_exp_ms";
const EMAIL_KEY = "gdrive_user_email";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const GOOGLE_DOC_MIME = "application/vnd.google-apps.document";

let tokenClient = null;
let pendingTokenPromise = null;

const loadGoogleScript = () =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Google script failed to load")));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google script failed to load"));
    document.head.appendChild(script);
  });

export const isGoogleDriveClientConfigured = () => Boolean(getClientId());

const getStoredToken = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const exp = Number(sessionStorage.getItem(TOKEN_EXP_KEY) || 0);
  if (token && Date.now() < exp - 60_000) return token;
  return null;
};

const storeToken = (accessToken, expiresInSeconds) => {
  sessionStorage.setItem(TOKEN_KEY, accessToken);
  sessionStorage.setItem(
    TOKEN_EXP_KEY,
    String(Date.now() + Number(expiresInSeconds || 3600) * 1000)
  );
};

const fetchUserEmail = async (accessToken) => {
  try {
    const res = await fetch("https://www.googleapis.com/oauth2/v2/userinfo", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const email = data?.email || null;
    if (email) sessionStorage.setItem(EMAIL_KEY, email);
    return email;
  } catch {
    return sessionStorage.getItem(EMAIL_KEY);
  }
};

export const getGoogleDriveClientStatus = () => {
  const token = getStoredToken();
  return {
    connected: Boolean(token),
    email: sessionStorage.getItem(EMAIL_KEY) || null,
  };
};

export const requestGoogleDriveAccessToken = ({ forceConsent = false } = {}) => {
  if (!getClientId()) {
    return Promise.reject(new Error("Google Client ID is not configured in frontend"));
  }
  if (pendingTokenPromise) return pendingTokenPromise;

  pendingTokenPromise = new Promise((resolve, reject) => {
    let settled = false;
    let focusTimer = null;
    let popupOpened = false;

    const cleanup = () => {
      clearTimeout(timeoutId);
      if (focusTimer) clearTimeout(focusTimer);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("blur", handleWindowBlur);
      pendingTokenPromise = null;
    };

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    const handleWindowBlur = () => {
      popupOpened = true;
    };

    const handleWindowFocus = () => {
      if (!popupOpened) return;
      focusTimer = setTimeout(() => {
        if (!settled) {
          finish(reject, new Error("Google sign-in was cancelled"));
        }
      }, 800);
    };

    const timeoutId = setTimeout(() => {
      finish(reject, new Error("Google sign-in was cancelled"));
    }, 120000);

    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);

    loadGoogleScript()
      .then(() => {
        tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: getClientId(),
          scope: DRIVE_SCOPE,
          callback: async (response) => {
            if (response.error) {
              finish(
                reject,
                new Error(response.error_description || response.error || "Google sign-in failed")
              );
              return;
            }
            try {
              storeToken(response.access_token, response.expires_in);
              await fetchUserEmail(response.access_token);
              finish(resolve, response.access_token);
            } catch (err) {
              finish(reject, err);
            }
          },
          error_callback: (err) => {
            finish(
              reject,
              new Error(err?.message || err?.type || "Google sign-in was cancelled")
            );
          },
        });
        tokenClient.requestAccessToken({
          prompt: forceConsent ? "consent" : "",
        });
      })
      .catch((err) => finish(reject, err));
  });

  return pendingTokenPromise;
};

export const ensureGoogleDriveAccessToken = async () => {
  const existing = getStoredToken();
  if (existing) return existing;
  return requestGoogleDriveAccessToken({ forceConsent: true });
};

const buildMultipartBody = (metadata, fileBytes, fileMime) => {
  const boundary = `boundary_${Date.now()}`;
  const metaPart = JSON.stringify(metadata);
  const encoder = new TextEncoder();
  const preamble = encoder.encode(
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${metaPart}\r\n` +
      `--${boundary}\r\nContent-Type: ${fileMime}\r\n\r\n`
  );
  const closing = encoder.encode(`\r\n--${boundary}--\r\n`);
  const body = new Uint8Array(preamble.length + fileBytes.length + closing.length);
  body.set(preamble, 0);
  body.set(fileBytes, preamble.length);
  body.set(closing, preamble.length + fileBytes.length);
  return { body, boundary };
};

export const uploadDocxToGoogleDrive = async ({
  accessToken,
  documentBase64,
  filename = "plan.docx",
}) => {
  if (!accessToken) throw new Error("Google access token missing");
  if (!documentBase64) throw new Error("Document data missing");

  const raw = atob(documentBase64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);

  const displayName = (filename || "plan.docx").replace(/\.docx$/i, "") || "plan";
  const metadata = { name: displayName, mimeType: GOOGLE_DOC_MIME };
  const { body, boundary } = buildMultipartBody(metadata, bytes, DOCX_MIME);

  const response = await fetch(
    "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink,name",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": `multipart/related; boundary=${boundary}`,
      },
      body,
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error?.message || "Google Drive upload failed");
  }

  const webLink =
    data.webViewLink ||
    (data.id ? `https://docs.google.com/document/d/${data.id}/edit` : "");

  return {
    google_docs_link: webLink,
    document_link: webLink,
    filename: data.name || displayName,
  };
};
